import * as React from "react";

import * as DTO from "../../../../interfaces";
import { VideoProjectServices } from "@/services";

import { useHistory } from "react-router";

import { URLS, useAccountSettings } from "@/utils";
import { Button, Spinner } from "@/components/ui";
import { ChevronDown, ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { Svg } from "@/assets/svg";
import moment from "moment";
import { cn } from "@/lib/utils";
import LibraryCard from "@/components/Cells/LibraryCard";

interface OwnProps {
  coachId: number;
  player: DTO.PlayerResumeResponse;
}

type Props = OwnProps;

const VideoProjectsList: React.FC<Props> = ({ player, coachId }) => {
  const history = useHistory();
  const [videoProjects, setVideoProjects] = React.useState<DTO.Page<DTO.PlayerVideoProjectResponse>>({ content: [] });
  const [loading, setLoading] = React.useState(false);
  const [display, setDisplay] = React.useState(true);
  const { hasWritePermission } = useAccountSettings();
  const withWritePermission = hasWritePermission(DTO.WebComponents.VIDEO_CATALYST);

  React.useEffect(() => {
    setLoading(true);
    VideoProjectServices.videoProjectByPlayer(coachId, player.playerId)
      .then(response => {
        setVideoProjects(cpages => {
          return {
            ...cpages!,
            ...response,
          };
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [coachId, player]);

  const loadMore = React.useCallback(
    async (pageOffset: number) => {
      if (loading) return;
      const { number, totalPages } = videoProjects;
      if (number === undefined || totalPages === undefined) return;
      const nextPage = number + pageOffset;
      if (nextPage >= totalPages || nextPage < 0) return;

      setLoading(true);
      const videoProjectResponse = await VideoProjectServices.videoProjectByPlayer(coachId, player.playerId, nextPage);

      setLoading(false);
      setVideoProjects(cpages => {
        return {
          ...cpages,
          ...videoProjectResponse,
          content: videoProjectResponse.content,
        };
      });
    },
    [coachId, player, loading]
  );

  const actionTitle = (): string => {
    var { totalElements, number } = videoProjects;
    number = number || 0;
    totalElements = totalElements || 0;
    if (!display) {
      return "Collapsed (" + totalElements + ")";
    }
    return `Showing ${number * 5} - ${number * 5 + videoProjects.content.length} of ${totalElements}`;
  };

  return (
    <div>
      <div
        className={cn(
          "flex items-center justify-between w-full p-2 border border-white text-foreground",
          display || "rounded-sm border-border bg-accent text-accent-foreground"
        )}
      >
        <div className="font-bold text-xl indent-1">{player.fullName}</div>
        <Button variant="ghost" size="xs" onClick={() => setDisplay(!display)}>
          {actionTitle()}
          {display ? <ChevronRight className="ml-2" /> : <ChevronDown className="ml-2" />}
        </Button>
      </div>
      {display && (
        <div className="flex gap-x-5 items-center flex-wrap">
          {videoProjects.first !== undefined && !videoProjects.first && (
            <Button
              variant="ghost"
              className="bg-accent"
              onClick={() => loadMore(-1)}
              disabled={loading}
              children={<ChevronLeft />}
            />
          )}

          {withWritePermission && (
            <Button
              variant="outline"
              className="bg-background hover:bg-background rounded-2 my-2 border border-border border-dashed w-38 h-65 hover:shadow-md flex flex-col items-center justify-center"
              onClick={() => {
                let locationValues = { playerId: player?.playerId };
                history.push(URLS.coach.newVideoProject + "?playerId=" + player?.playerId, locationValues);
              }}
            >
              <Plus className="border rounded-full border-border size-7 p-1" />
              Add new
            </Button>
          )}
          {loading && videoProjects.content.length === 0 ? (
            <Spinner className="size-12" />
          ) : (
            videoProjects.content.map(videoProject => {
              return (
                <LibraryCard
                  key={"video_project_card_" + videoProject.videoProjectId}
                  title={videoProject.statusLabel}
                  thumbnail={videoProject.thumbnail?.thumbnailUri || Svg.PromptPlaceholder}
                  dateLabel={moment(videoProject.statusDate).format("MMM DD, YY [at] hh:mm a")}
                  className={cn("h-65 min-h-65", videoProject.needsAck && "border-warning")}
                  disabled={loading}
                  onClick={() => history.push(URLS.coach.videoProject.replace(":id", `${videoProject.videoProjectId}`))}
                />
              );
            })
          )}
          {videoProjects.last !== undefined && !videoProjects.last && (
            <Button
              variant="ghost"
              className="bg-accent ml-auto"
              onClick={() => loadMore(1)}
              disabled={loading}
              children={<ChevronRight />}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default VideoProjectsList;
